import { create } from 'zustand';
import { io } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3001';

export const useSocketStore = create((set, get) => ({
  socket: null,
  isConnected: false,
  notifications: [],
  alerts: [],

  connect: (token) => {
    const existingSocket = get().socket;
    if (existingSocket?.connected) {
      return;
    }

    const socket = io(SOCKET_URL, {
      auth: { token },
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socket.on('connect', () => {
      console.log('Socket connected');
      set({ isConnected: true });
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      set({ isConnected: false });
    });

    socket.on('alert:new', (alert) => {
      set((state) => ({
        alerts: [alert, ...state.alerts].slice(0, 50),
        notifications: [
          {
            id: Date.now(),
            type: 'alert',
            title: alert.title,
            message: alert.message,
            severity: alert.severity,
            timestamp: new Date(),
          },
          ...state.notifications,
        ].slice(0, 20),
      }));
    });

    socket.on('alert:critical', (alert) => {
      set((state) => ({
        notifications: [
          {
            id: Date.now(),
            type: 'critical',
            title: `⚠️ CRITICAL: ${alert.title}`,
            message: alert.message,
            severity: 'critical',
            timestamp: new Date(),
          },
          ...state.notifications,
        ].slice(0, 20),
      }));
    });

    socket.on('workflow:assigned', (workflow) => {
      set((state) => ({
        notifications: [
          {
            id: Date.now(),
            type: 'workflow',
            title: 'New Workflow Assigned',
            message: workflow.title,
            timestamp: new Date(),
          },
          ...state.notifications,
        ].slice(0, 20),
      }));
    });

    socket.on('prediction:high_risk', (prediction) => {
      set((state) => ({
        notifications: [
          {
            id: Date.now(),
            type: 'prediction',
            title: 'High Risk Prediction',
            message: prediction.description,
            severity: prediction.riskLevel,
            timestamp: new Date(),
          },
          ...state.notifications,
        ].slice(0, 20),
      }));
    });

    socket.on('maintenance:started', (data) => {
      set((state) => ({
        notifications: [
          {
            id: Date.now(),
            type: 'maintenance',
            title: 'Maintenance Started',
            message: `Maintenance started on machine`,
            timestamp: new Date(),
          },
          ...state.notifications,
        ].slice(0, 20),
      }));
    });

    set({ socket });
  },

  disconnect: () => {
    const socket = get().socket;
    if (socket) {
      socket.disconnect();
      set({ socket: null, isConnected: false });
    }
  },

  joinMachineRoom: (machineId) => {
    const socket = get().socket;
    if (socket) {
      socket.emit('join:machine', machineId);
    }
  },

  leaveMachineRoom: (machineId) => {
    const socket = get().socket;
    if (socket) {
      socket.emit('leave:machine', machineId);
    }
  },

  subscribeSensors: (machineId) => {
    const socket = get().socket;
    if (socket) {
      socket.emit('subscribe:sensors', machineId);
    }
  },

  unsubscribeSensors: (machineId) => {
    const socket = get().socket;
    if (socket) {
      socket.emit('unsubscribe:sensors', machineId);
    }
  },

  clearNotification: (id) => {
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clearAllNotifications: () => {
    set({ notifications: [] });
  },
}));
